/**
 * Escopo das telas do WhatsApi (spec 2026-11-02 §5.1, que substitui a §5.3 da de 23/10), em ordem:
 *  1. escolha manual na tela (`escolher`);
 *  2. "Organização ativa" do menu, se estiver em GET /v1/whatsapi/organizacoes — mesmo com tenant;
 *  3. tenant;
 *  4. a primeira organização da lista;
 *  5. nenhum.
 *
 * Quem tem tenant E organização escolhe na tela com `escolher`. A escolha é local à
 * tela: não muda o seletor global do menu.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { whatsapiOrgApi, type OrgWhatsApi } from '@/api/whatsapiOrg'
import { useAuthStore, useCurrentTenant } from '@/store/auth'

export type EscopoWhatsApi =
  | { tipo: 'tenant'; tenantId: number }
  | { tipo: 'organizacao'; orgId: number; pode: OrgWhatsApi['pode'] }
  | { tipo: 'nenhum' }

export interface EscopoWhatsApiState {
  escopo: EscopoWhatsApi
  opcoes: { tenant: boolean; organizacoes: OrgWhatsApi[] }
  escolher: (tipo: 'tenant' | 'organizacao', orgId?: number) => void
  /** Nome do escopo resolvido (organização da lista ou tenant), para o título (spec 2026-11-02 §5.5). */
  nome: string | null
  /** A lista de organizações ainda não chegou e decide o escopo (sem tenant, ou com organização ativa). */
  carregando: boolean
}

const SEM_ORGANIZACAO: OrgWhatsApi[] = []

/** GET /v1/whatsapi/organizacoes: as organizações com `whatsapi:read` e o `pode` de cada uma.
 *  Uma consulta só (mesma chave) para o escopo das telas, o item Conversas do menu e a seção
 *  "Atendimento humano" do gateway (`pode.atender`). */
export function useOrganizacoesWhatsApi({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['whatsapi-organizacoes'],
    queryFn: whatsapiOrgApi.organizacoes,
    retry: false,
    enabled,
  })
}

export function useEscopoWhatsApi(): EscopoWhatsApiState {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId)
  const [escolha, setEscolha] = useState<{ tipo: 'tenant' | 'organizacao'; orgId?: number } | null>(null)

  const { data: organizacoes = SEM_ORGANIZACAO, isPending } = useOrganizacoesWhatsApi()

  const escopo = useMemo<EscopoWhatsApi>(() => {
    const daOrg = (o: OrgWhatsApi): EscopoWhatsApi => ({ tipo: 'organizacao', orgId: o.id, pode: o.pode })
    const ativa = organizacoes.find((o) => o.id === selectedOrgId)
    if (escolha?.tipo === 'tenant' && tenantId != null) return { tipo: 'tenant', tenantId }
    if (escolha?.tipo === 'organizacao') {
      const escolhida = organizacoes.find((o) => o.id === escolha.orgId) ?? ativa ?? organizacoes[0]
      if (escolhida) return daOrg(escolhida)
    }
    if (ativa) return daOrg(ativa)
    if (tenantId != null) return { tipo: 'tenant', tenantId }
    if (organizacoes[0]) return daOrg(organizacoes[0])
    return { tipo: 'nenhum' }
  }, [organizacoes, selectedOrgId, escolha, tenantId])

  const opcoes = useMemo(
    () => ({ tenant: tenantId != null, organizacoes }),
    [tenantId, organizacoes],
  )

  const nome = escopo.tipo === 'organizacao'
    ? organizacoes.find((o) => o.id === escopo.orgId)?.name ?? null
    : escopo.tipo === 'tenant' ? tenant?.name ?? null : null

  return {
    escopo,
    nome,
    opcoes,
    escolher: (tipo, orgId) => setEscolha({ tipo, orgId }),
    carregando: isPending && (tenantId == null || (selectedOrgId != null && escolha?.tipo !== 'tenant')),
  }
}
